import React, { useState, useEffect } from 'react';
import { 
  Settings, Users, BookOpen, Calendar, ToggleLeft, ToggleRight,
  TrendingUp, Activity, Save, AlertCircle
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const SuperToolAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    is_enabled: false,
    account_book_enabled: true,
    calendar_enabled: true,
    max_parties_per_user: 100,
    max_tasks_per_user: 500
  });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/supertool/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/supertool/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleToggle = async () => {
    try {
      const response = await api.post('/admin/supertool/toggle');
      setSettings({ ...settings, is_enabled: response.data.is_enabled });
      toast.success(response.data.is_enabled ? 'SuperTool Enabled!' : 'SuperTool Disabled');
    } catch (error) {
      toast.error('Failed to toggle SuperTool');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/supertool/settings', settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4" data-testid="supertool-admin-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">SuperTool Settings</h1>
          <p className="text-muted-foreground">Manage Account Book & Calendar for all members</p>
        </div>

        {/* Main Toggle */}
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                settings.is_enabled ? 'bg-green-500/20' : 'bg-red-500/20'
              }`}>
                <Settings className={`w-7 h-7 ${settings.is_enabled ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div>
                <h2 className="text-xl font-bold">SuperTool</h2>
                <p className="text-sm text-muted-foreground">
                  {settings.is_enabled ? 'Enabled for all members' : 'Disabled - Members cannot access'}
                </p>
              </div>
            </div>
            <button
              onClick={handleToggle}
              className={`p-3 rounded-xl transition-all ${
                settings.is_enabled 
                  ? 'bg-green-500 text-white' 
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {settings.is_enabled ? (
                <ToggleRight className="w-8 h-8" />
              ) : (
                <ToggleLeft className="w-8 h-8" />
              )}
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-primary" />
                <span className="text-xs text-muted-foreground">Total Users</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_users}</p>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <BookOpen className="w-4 h-4 text-green-500" />
                <span className="text-xs text-muted-foreground">Total Parties</span>
              </div>
              <p className="text-2xl font-bold">{stats.account_book?.total_parties || 0}</p>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-muted-foreground">Transactions</span>
              </div>
              <p className="text-2xl font-bold">{stats.account_book?.total_transactions || 0}</p>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-yellow-500" />
                <span className="text-xs text-muted-foreground">Total Tasks</span>
              </div>
              <p className="text-2xl font-bold">{stats.calendar?.total_tasks || 0}</p>
            </div>
          </div>
        )}

        {/* Feature Settings */}
        <div className="glass-card p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Feature Settings</h3>
          
          <div className="space-y-4">
            {/* Account Book Toggle */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-green-500" />
                <div>
                  <p className="font-medium">Account Book</p>
                  <p className="text-xs text-muted-foreground">Khatabook-style ledger for members</p>
                </div>
              </div>
              <button
                onClick={() => setSettings({ ...settings, account_book_enabled: !settings.account_book_enabled })}
                className={`w-12 h-6 rounded-full transition-all ${
                  settings.account_book_enabled ? 'bg-green-500' : 'bg-white/20'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  settings.account_book_enabled ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            {/* Calendar Toggle */}
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium">Calendar & Tasks</p>
                  <p className="text-xs text-muted-foreground">Task management and appointments</p>
                </div>
              </div>
              <button
                onClick={() => setSettings({ ...settings, calendar_enabled: !settings.calendar_enabled })}
                className={`w-12 h-6 rounded-full transition-all ${
                  settings.calendar_enabled ? 'bg-green-500' : 'bg-white/20'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                  settings.calendar_enabled ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        </div>

        {/* Limits */}
        <div className="glass-card p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Usage Limits (Per User)</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Max Parties</label>
              <input
                type="number"
                value={settings.max_parties_per_user}
                onChange={(e) => setSettings({ ...settings, max_parties_per_user: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Max Tasks</label>
              <input
                type="number"
                value={settings.max_tasks_per_user}
                onChange={(e) => setSettings({ ...settings, max_tasks_per_user: parseInt(e.target.value) })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50"
        >
          {saving ? (
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <>
              <Save className="w-5 h-5" />
              Save Settings
            </>
          )}
        </button>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-500">How SuperTool Works</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• <strong>Account Book:</strong> Members can track credit/debit with customers & vendors</li>
                <li>• <strong>Calendar:</strong> Members can manage tasks, appointments & to-do lists</li>
                <li>• Toggle the main switch to enable/disable for all members</li>
                <li>• Each member's data is private and separate</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperToolAdmin;
